import { SourceTypes } from '../../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../../utils';
import type { DataValue, ProviderType } from '../../types';
import type { InstagramSourceValue } from '../types';
import type { LegacyInstagramResponseType } from './types';
import { getInstagramMedia } from './utils';

const SOURCE_TYPE = SourceTypes.INSTAGRAM;

const REQUEST_FIELDS = [
  'caption',
  'comments_count',
  'id',
  'like_count',
  'media_type',
  'media_url',
  'permalink',
  'thumbnail_url',
  'timestamp',
  'username',
  'children{media_type,media_url,thumbnail_url}'
];

const select = (response: LegacyInstagramResponseType): DataValue[] => {
  handleResponseError(response, SOURCE_TYPE, 'data');

  return response.data.map(
    ({
      id,
      caption,
      permalink,
      timestamp,
      username,
      children,
      like_count: likeCount,
      comments_count: commentsCount,
      media_type: mediaType,
      media_url: mediaUrl,
      thumbnail_url: thumbnailUrl
    }) => {
      const text = formatText(caption, SOURCE_TYPE);
      const media = getInstagramMedia({
        media_type: mediaType,
        media_url: mediaUrl,
        thumbnail_url: thumbnailUrl,
        children
      });

      return {
        id,
        text,
        media,
        timestamp: Date.parse(timestamp),
        source: {
          type: SOURCE_TYPE,
          url: permalink
        },
        user: {
          username,
          url: `https://instagram.com/${username}`
        },
        counters: {
          likes: likeCount,
          comments: commentsCount
        }
      };
    }
  );
};

export function createInstagramBusinessAccountProvider(
  { authentication }: InstagramSourceValue<'legacyInstagramBusinessAccount'>,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<LegacyInstagramResponseType> {
  const { identity = '', data } = authentication ?? {};
  const id = data?.instagram?.id ?? '';

  const key = [...globalKey, ...filtersKey, id, identity];

  const page = (data: LegacyInstagramResponseType | undefined) => {
    if (data) {
      return data.paging?.next;
    }

    const requestParams = new URLSearchParams({
      fields: REQUEST_FIELDS.join(',')
    });
    const requestURL = `${id}/media?${requestParams}`;

    return buildProxyURL({
      apiSource: 'facebook',
      params: { q: requestURL, user_id: identity }
    });
  };

  return { key, select, page };
}
